import { ApiError } from 'api/error/ApiError';
import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

const axiosInstance = axios.create();

let requestInterceptorId: number | null = null;
let abortInterceptorId: number | null = null;

export * from 'axios';

export default axiosInstance;

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _isRetry?: boolean;
}

export const setupAuthInterceptor = (
  getAuthToken: () => string | undefined,
  getSessionId: () => string | null,
  getAdminSessionId: () => string | null,
  onTokenExpired: () => Promise<void>,
  onUnauthorized: () => Promise<void>
) => {
  if (requestInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
  }

  if (abortInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(abortInterceptorId);
  }

  abortInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      const controller = new AbortController();
  
      if (!config.headers['Authorization']) {
        controller.abort("Authorization header is required!");
      }

      return {
        ...config,
        signal: controller.signal
      };
    },
    (error) => {
      return Promise.reject(error);
    }
  )

  requestInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      const token = getAuthToken();
      const sessionId = getSessionId();
      const adminSessionId = getAdminSessionId();

      config.headers = config.headers || {};

      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      if (sessionId) {
        config.headers['X-Session-ID'] = sessionId;
      }

      if (adminSessionId && adminSessionId !== sessionId) {
        config.headers['X-Admin-Session-ID'] = adminSessionId;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle token expiration and unauthorized requests
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      // Handle token expiration (e.g., 401 status code)
      if (error.response?.status === 401 && originalRequest && !originalRequest._isRetry) {
        originalRequest._isRetry = true;
        try {
          await onTokenExpired();

          const newToken = getAuthToken();
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          // If token refresh fails, log out the user
          await onUnauthorized();
          return Promise.reject(refreshError);
        }
      }

      // if there's an API errors array in the response, return the first error
      // @ts-expect-error
      if (error.response?.data?.errors?.length) {
        return Promise.reject(ApiError.fromAxiosResponse(error));
      }

      return Promise.reject(error);
    }
  );
};